import React from "react";
import { X, Calendar, User, FileText, FolderOpen, Bot, Shield, Users } from "lucide-react";
import { DeletedPost, DeletedCategory, DeletedBot, DeletedTerm, DeletedPrivacyPolicy, DeletedUser } from "../services/trashService";
import { formatDateTime } from "../utils/dateFormatter";
import "../styles/components/trash-detail-modal.css";

interface TrashDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: DeletedPost | DeletedCategory | DeletedBot | DeletedTerm | DeletedPrivacyPolicy | DeletedUser | null;
  type: 'post' | 'category' | 'bot' | 'term' | 'privacy-policy' | 'user';
}

const TrashDetailModal: React.FC<TrashDetailModalProps> = ({ isOpen, onClose, item, type }) => {
  if (!isOpen || !item) return null;

  const renderPostDetails = (post: DeletedPost) => (
    <div className="trash-detail-modal__content">
      <div className="trash-detail-modal__header">
        <div className="trash-detail-modal__icon">
          <FileText size={24} />
        </div>
        <div className="trash-detail-modal__title-section">
          <h2 className="trash-detail-modal__title">{post.title}</h2>
          <span className="trash-detail-modal__status trash-detail-modal__status--deleted">Đã xóa</span>
        </div>
      </div>

      <div className="trash-detail-modal__meta">
        <div className="trash-detail-modal__meta-item">
          <User size={16} />
          <span><strong>Tác giả:</strong> {post.author}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <Calendar size={16} />
          <span><strong>Xóa lúc:</strong> {formatDateTime(post.deletedAt)}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <span><strong>Xóa bởi:</strong> {post.deletedBy}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <span><strong>Danh mục:</strong> {post.category}</span>
        </div>
        {post.slug && (
          <div className="trash-detail-modal__meta-item">
            <span><strong>Slug:</strong> {post.slug}</span>
          </div>
        )}
      </div>

      {post.featuredImage && (
        <div className="trash-detail-modal__image">
          <img src={post.featuredImage} alt={post.title} />
        </div>
      )}

      <div className="trash-detail-modal__content-section">
        <h3>Nội dung bài viết</h3>
        <div className="trash-detail-modal__content-text">
          {post.content}
        </div>
      </div>
    </div>
  );

  const renderCategoryDetails = (category: DeletedCategory) => (
    <div className="trash-detail-modal__content">
      <div className="trash-detail-modal__header">
        <div className="trash-detail-modal__icon">
          <FolderOpen size={24} />
        </div>
        <div className="trash-detail-modal__title-section">
          <h2 className="trash-detail-modal__title">{category.name}</h2>
          <span className="trash-detail-modal__status trash-detail-modal__status--deleted">Đã xóa</span>
        </div>
      </div>

      <div className="trash-detail-modal__meta">
        <div className="trash-detail-modal__meta-item">
          <User size={16} />
          <span><strong>Xóa bởi:</strong> {category.deletedBy}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <Calendar size={16} />
          <span><strong>Xóa lúc:</strong> {formatDateTime(category.deletedAt)}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <span><strong>Số bài viết:</strong> {category.postCount}</span>
        </div>
        {category.slug && (
          <div className="trash-detail-modal__meta-item">
            <span><strong>Slug:</strong> {category.slug}</span>
          </div>
        )}
      </div>

      <div className="trash-detail-modal__content-section">
        <h3>Mô tả danh mục</h3>
        <div className="trash-detail-modal__content-text">
          {category.description}
        </div>
      </div>
    </div>
  );

  const renderBotDetails = (bot: DeletedBot) => (
    <div className="trash-detail-modal__content">
      <div className="trash-detail-modal__header">
        <div className="trash-detail-modal__icon">
          <Bot size={24} />
        </div>
        <div className="trash-detail-modal__title-section">
          <h2 className="trash-detail-modal__title">{bot.name}</h2>
          <span className="trash-detail-modal__status trash-detail-modal__status--deleted">Đã xóa</span>
        </div>
      </div>

      <div className="trash-detail-modal__meta">
        <div className="trash-detail-modal__meta-item">
          <User size={16} />
          <span><strong>Xóa bởi:</strong> {bot.deletedBy}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <Calendar size={16} />
          <span><strong>Xóa lúc:</strong> {formatDateTime(bot.deletedAt)}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <span><strong>Trạng thái trước khi xóa:</strong> {bot.isActive ? "Hoạt động" : "Tạm dừng"}</span>
        </div>
      </div>

      <div className="trash-detail-modal__content-section">
        <h3>Mô tả bot</h3>
        <div className="trash-detail-modal__content-text">
          {bot.description}
        </div>
      </div>

      <div className="trash-detail-modal__content-section">
        <h3>Từ khóa kiểm duyệt</h3>
        <div className="trash-detail-modal__keywords">
          {bot.keywords.map((keyword, index) => (
            <span key={index} className="trash-detail-modal__keyword">
              {keyword}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTermDetails = (term: DeletedTerm) => (
    <div className="trash-detail-modal__content">
      <div className="trash-detail-modal__header">
        <div className="trash-detail-modal__icon">
          <Shield size={24} />
        </div>
        <div className="trash-detail-modal__title-section">
          <h2 className="trash-detail-modal__title">{term.title}</h2>
          <span className="trash-detail-modal__status trash-detail-modal__status--deleted">Đã xóa</span>
        </div>
      </div>

      <div className="trash-detail-modal__meta">
        <div className="trash-detail-modal__meta-item">
          <User size={16} />
          <span><strong>Xóa bởi:</strong> {term.deletedBy}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <Calendar size={16} />
          <span><strong>Xóa lúc:</strong> {formatDateTime(term.deletedAt)}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <span><strong>Phiên bản:</strong> {term.version}</span>
        </div>
        <div className="trash-detail-modal__meta-item">
          <span><strong>Trạng thái trước khi xóa:</strong> {term.isActive ? "Hoạt động" : "Tạm dừng"}</span>
        </div>
      </div>

      <div className="trash-detail-modal__content-section">
        <h3>Nội dung điều khoản</h3>
        <div className="trash-detail-modal__content-text">
          {term.content}
        </div>
      </div>
    </div>
  );

  const renderUserDetails = (user: DeletedUser) => {
    const getRoleLabel = (role: string) => {
      const roleLabels: { [key: string]: string } = {
        'MEMBER': 'Thành viên',
        'PREMIUM': 'Thành viên Premium',
        'TOOL_USER': 'Người dùng công cụ',
        'MODERATOR': 'Kiểm duyệt viên',
        'ADMIN': 'Quản trị viên'
      };
      return roleLabels[role] || role;
    };

    return (
      <div className="trash-detail-modal__content">
        <div className="trash-detail-modal__header">
          <div className="trash-detail-modal__icon">
            <Users size={24} />
          </div>
          <div className="trash-detail-modal__title-section">
            <h2 className="trash-detail-modal__title">{user.fullName}</h2>
            <span className="trash-detail-modal__status trash-detail-modal__status--deleted">Đã xóa</span>
          </div>
        </div>

        <div className="trash-detail-modal__meta">
          <div className="trash-detail-modal__meta-item">
            <User size={16} />
            <span><strong>Xóa bởi:</strong> {user.deletedBy}</span>
          </div>
          <div className="trash-detail-modal__meta-item">
            <Calendar size={16} />
            <span><strong>Xóa lúc:</strong> {formatDateTime(user.deletedAt)}</span>
          </div>
          <div className="trash-detail-modal__meta-item">
            <span><strong>Tên đăng nhập:</strong> {user.username}</span>
          </div>
          <div className="trash-detail-modal__meta-item">
            <span><strong>Email:</strong> {user.email}</span>
          </div>
          <div className="trash-detail-modal__meta-item">
            <span><strong>Vai trò:</strong> {getRoleLabel(user.role)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderPrivacyPolicyDetails = (policy: DeletedPrivacyPolicy) => {
    return (
      <div className="trash-detail-modal__content">
        <div className="trash-detail-modal__header">
          <div className="trash-detail-modal__icon">
            <Shield size={24} />
          </div>
          <div className="trash-detail-modal__title-section">
            <h2 className="trash-detail-modal__title">{policy.title}</h2>
            <span className="trash-detail-modal__status trash-detail-modal__status--deleted">Đã xóa</span>
          </div>
        </div>

        <div className="trash-detail-modal__meta">
          <div className="trash-detail-modal__meta-item">
            <User size={16} />
            <span><strong>Xóa bởi:</strong> {policy.deletedBy}</span>
          </div>
          <div className="trash-detail-modal__meta-item">
            <Calendar size={16} />
            <span><strong>Xóa lúc:</strong> {formatDateTime(policy.deletedAt)}</span>
          </div>
          <div className="trash-detail-modal__meta-item">
            <span><strong>Phiên bản:</strong> {policy.version}</span>
          </div>
          <div className="trash-detail-modal__meta-item">
            <span><strong>Trạng thái:</strong> {policy.isActive ? "Hoạt động" : "Tạm dừng"}</span>
          </div>
          {policy.filePath && (
            <div className="trash-detail-modal__meta-item">
              <span><strong>File đính kèm:</strong> {policy.originalFileName || "Có file"}</span>
            </div>
          )}
        </div>

        <div className="trash-detail-modal__content-section">
          <h3 className="trash-detail-modal__section-title">Nội dung</h3>
          <div className="trash-detail-modal__content-text">
            {policy.content || "Không có nội dung text"}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (type) {
      case 'post':
        return renderPostDetails(item as DeletedPost);
      case 'category':
        return renderCategoryDetails(item as DeletedCategory);
      case 'bot':
        return renderBotDetails(item as DeletedBot);
      case 'term':
        return renderTermDetails(item as DeletedTerm);
      case 'privacy-policy':
        return renderPrivacyPolicyDetails(item as DeletedPrivacyPolicy);
      case 'user':
        return renderUserDetails(item as DeletedUser);
      default:
        return null;
    }
  };

  return (
    <div className="trash-detail-modal-overlay" onClick={onClose}>
      <div className="trash-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="trash-detail-modal__header-actions">
          <button 
            className="trash-detail-modal__close-btn"
            onClick={onClose}
            title="Đóng"
          >
            <X size={20} />
          </button>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default TrashDetailModal;
